import { useCallback, useEffect, useRef } from 'react';

const defaultTimeoutAction = () => {};

const getElement = (selector) => {
  // ✅ Add safety check - return null if no selector
  if (!selector) {
    return null;
  }

  const element = document.querySelector(selector);
  // ✅ Return null instead of throwing error during cleanup
  if (!element) {
    console.warn(`Element not found: ${selector}`);
    return null;
  }
  return element;
};

function useIdleTimer({
  timeout = 20 * 60 * 1000,
  auto = true,
  selector,
  events = [],
  onTimeout = defaultTimeoutAction,
  onRefresh = () => {},
}) {
  const timer = useRef(null);
  const isMountedRef = useRef(true); // ✅ Track if component is mounted

  const start = useCallback(() => {
    if (!selector) {
      console.warn('No selector provided for the idle timer.');
      return;
    }

    // ✅ Get element safely
    const element = getElement(selector);
    if (!element) {
      console.warn('Cannot start idle timer - element not found');
      return;
    }

    events.forEach((eventName) => {
      element.addEventListener(eventName, refresh.current);
    });
    timer.current = setTimeout(onTimeout, timeout);
  }, [timeout, onTimeout, selector, ...events]); // ✅ Fix dependencies

  const clean = useCallback(() => {
    if (!selector) {
      return;
    }

    // ✅ Get element safely during cleanup
    const element = getElement(selector);
    if (!element) {
      // ✅ Don't throw error during cleanup - element might be removed
      return;
    }

    events.forEach((eventName) => {
      try {
        element.removeEventListener(eventName, refresh.current);
      } catch (error) {
        // ✅ Silently handle removal errors
        console.warn('Error removing event listener:', error);
      }
    });
  }, [selector, ...events]); // ✅ Fix dependencies

  const cancel = useCallback(() => {
    // ✅ Clear timeout safely
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    clean();
  }, [clean]);

  const refresh = useRef(() => {
    // ✅ Check if component is still mounted
    if (!isMountedRef.current) {
      return;
    }

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // ✅ Check again before calling onTimeout
      if (isMountedRef.current && typeof onTimeout === 'function') {
        onTimeout();
      }
    }, timeout);

    if (typeof onRefresh === 'function') {
      onRefresh();
    }
  });

  useEffect(() => {
    isMountedRef.current = true; // ✅ Mark as mounted

    if (auto) {
      start();
    }

    // ✅ Enhanced cleanup function
    return () => {
      isMountedRef.current = false; // ✅ Mark as unmounted
      cancel();
    };
  }, [auto, start, cancel]);

  // ✅ Additional cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, []);

  return {
    refresh: refresh.current,
    start,
    cancel,
  };
}

export default useIdleTimer;
